import type { DeepReadonly, InjectionKey, Ref } from 'vue'
import type { TenantUser } from '../TenantUser'
import type { TenantOwnedDatabase } from '../TenantOwnedDatabase'
import type { DatabaseAccessGrant } from '../DatabaseAccessGrant'
import type {
  AddTenantUserRequest,
  ActivateUserRequest,
  DeactivateUserRequest,
  ScheduleUserDeactivationRequest,
  ChangeTenantUserEmailRequest,
  ChangeTenantUserTitleRequest,
  ChangeTenantUserNameRequest,
  ChangeTenantUserPhoneNumberRequest,
  ChangeTenantUserProfileImageRequest,
  GrantTenantAdminPrivilegesRequest,
  RevokeTenantAdminPrivilegesRequest
} from '../api-requests/TenantUserRequests'

/**
 * Data source for the collection of all tenant users.
 *
 * Runtime-free contract: the concrete `useTenantUsersCollection` composable
 * additionally wires `useTenantUserSignalREvents` internally — that handle is
 * not part of the returned object, so there is nothing to omit here.
 */
export interface TenantUsersCollectionService {
  tenantUsers: Ref<TenantUser[]>
  fetchTenantUsers(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }): Promise<void>
  commands: {
    addTenantUser(
      payload: AddTenantUserRequest,
      previewImageUrl: string | undefined
    ): Promise<void>
  }
}

/**
 * Data source for a single tenant user's detail + per-user commands.
 *
 * The `modals` member the admin shell appends is UI (template refs) and is
 * intentionally absent from this contract.
 */
export interface TenantUserDetailService {
  tenantUser: Ref<TenantUser | undefined>
  commands: {
    activateUser(payload: ActivateUserRequest): Promise<void>
    deactivateUser(payload: DeactivateUserRequest): Promise<void>
    changeEmail(payload: ChangeTenantUserEmailRequest): Promise<void>
    removeDeactivationSchedule(
      payload: ScheduleUserDeactivationRequest
    ): Promise<void>
    changeJobTitle(payload: ChangeTenantUserTitleRequest): Promise<void>
    changeName(payload: ChangeTenantUserNameRequest): Promise<void>
    changePhoneNumber(
      payload: ChangeTenantUserPhoneNumberRequest
    ): Promise<void>
    changeProfileImage(
      payload: ChangeTenantUserProfileImageRequest
    ): Promise<void>
    scheduleDeactivation(
      payload: ScheduleUserDeactivationRequest
    ): Promise<void>
    grantTenantAdminPrivileges(
      payload: GrantTenantAdminPrivilegesRequest
    ): Promise<void>
    revokeTenantAdminPrivileges(
      payload: RevokeTenantAdminPrivilegesRequest
    ): Promise<void>
  }
}

/** Data source for the databases a tenant owns. */
export interface TenantOwnedDatabasesService {
  allTenantDatabases: Ref<TenantOwnedDatabase[]>
  getDatabaseAccessItems(userId: string): TenantOwnedDatabase[]
  fetchData(options?: {
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }): Promise<{ databases: DeepReadonly<TenantOwnedDatabase[]> }>
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
}

/**
 * Data source for a tenant user's database-access grants.
 *
 * Runtime-free contract: the concrete composable holds a
 * `useTenantUserSignalREvents` handle internally (not returned), so only the
 * plain subscribe/unsubscribe functions appear here.
 */
export interface TenantUserDatabaseAccessGrantsService {
  databaseAccessGrants: Ref<DatabaseAccessGrant[]>
  fetchData(
    tenantUserId: string,
    options?: { forceFetch?: boolean; subscribeToEvents?: boolean }
  ): Promise<{ databaseAccessGrants: DeepReadonly<DatabaseAccessGrant[]> }>
  addDatabaseAccessGrant(grant: DatabaseAccessGrant): void
  updateDatabaseAccessGrant(
    grantId: string,
    updatedGrant: DatabaseAccessGrant
  ): void
  removeDatabaseAccessGrant(grantId: string): void
  subscribeToDatabaseAccessEvents(tenantUserId: string): () => void
  unsubscribeFromDatabaseAccessEvents(): void
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
}

export const tenantUsersCollectionKey = Symbol(
  'gs-tenant-users-collection'
) as InjectionKey<TenantUsersCollectionService>

export const tenantUserDetailKey = Symbol(
  'gs-tenant-user-detail'
) as InjectionKey<TenantUserDetailService>

export const tenantOwnedDatabasesKey = Symbol(
  'gs-tenant-owned-databases'
) as InjectionKey<TenantOwnedDatabasesService>

export const tenantUserDatabaseAccessGrantsKey = Symbol(
  'gs-tenant-user-database-access-grants'
) as InjectionKey<TenantUserDatabaseAccessGrantsService>
